import { Component, Input, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { UptimeRepoService } from '@client/app/services/api/uptime-repo/uptime-repo.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import {
    FormUptimeScheduledDowntimeComponent,
} from '@client/app/forms/uptime/scheduled_downtimes/scheduled_downtime/scheduled_downtime.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'form-uptime-scheduled-downtimes',
    standalone: false,
    template: `
        <div class="mon-dialog-header">
            <page-header header="{{'Checkpoint exemptions' | translate }}" subHeader="{{'Checkpoint settings'}}">
                <page-header-content>
                    <div class="row">
                        <div class="col-48 text-right outer-t-smurf">
                            <button type="button"
                                    (click)="openExemption()"
                                    class="btn btn-border-radius btn-secondary outer-t-smurf" translate>
                                Add new exemption
                            </button>
                        </div>
                    </div>
                </page-header-content>
            </page-header>
        </div>

        <div class="mon-dialog-body bg-body">
            <div class="row">
                <div class="col-48">
                    <div class="card panel-monsido-card outer-t outer-r outer-l">
                        <div class="card-header">
                            <h3 class="font-weight-300" translate>
                                Exemptions
                            </h3>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-48">
                                    <table-container [collection]="downtimeSchedules"
                                                     noResultMessage="{{'No exemptions added to this checkpoint' | translate}}"
                                                     (onPageChange)="pageChange($event)"
                                                     (onPerPageChange)="perPageChange($event)"
                                                     [progress]="progress">
                                        <table class="table table-fixed table-hover">
                                            <thead>
                                            <tr>
                                                <th class="col-md-9 text-left" translate>Start at</th>
                                                <th class="col-md-9 text-left" translate>End at</th>
                                                <th class="col-md-10 text-left" translate>Repeat</th>
                                                <th class="col-md-11 text-left" translate>Part of downtime calculation</th>
                                                <th class="col-md-9 text-right" translate>Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr *ngFor="let downtimeSchedule of downtimeSchedules">
                                                <td class="text-v-middle">
                                                    <user-date [twoLines]="true"
                                                               [date]="downtimeSchedule.current_start_at">
                                                    </user-date>
                                                </td>
                                                <td class="text-v-middle">
                                                    <user-date [twoLines]="true"
                                                               [date]="downtimeSchedule.current_end_at">
                                                    </user-date>
                                                </td>
                                                <td class="text-v-middle">
                                                    {{getRecurringTranslation(downtimeSchedule.recurring)}}
                                                </td>
                                                <td class="Text-center text-v-middle">
                                                    <span class="fa" [ngClass]="{
                                                    'text-success fa-check-o': downtimeSchedule.register_downtime,
                                                    'text-danger fa-times': !downtimeSchedule.register_downtime
                                                    }"></span>
                                                </td>
                                                <td class="text-right text-v-middle">
                                                    <button type="button"
                                                            class="btn btn-border-radius btn-secondary"
                                                            (click)="openExemption(downtimeSchedule)">
                                                        <span class="fas fa-cog"></span>
                                                    </button>
                                                    <button type="button"
                                                            class="btn btn-border-radius btn-secondary"
                                                            (click)="removeScheduledDowntime(downtimeSchedule)">
                                                        <span class="fas fa-trash-alt"></span>
                                                    </button>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </table-container>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class FormUptimeScheduledDowntimesComponent extends BaseContainerComponentController implements OnInit {
    @Input() uptimeId: any;
    @Input() monDialogId: any;

    downtimeSchedules: any;
    progress: any;

    constructor (
        private monEventService: MonEventService,
        private uptimeRepoService: UptimeRepoService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
    ) {
        super({ table: true });
    }

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.getPage = this.loadPage.bind(this);
        this.loadPage();
    }

    loadPage (): void {
        const params = {
            page: this.page,
            page_size: this.pageSize,
        };

        this.progress = this.uptimeRepoService.getAllScheduledDowntimes(this.uptimeId, params).then(
            (schedules) => {
                this.downtimeSchedules = schedules;
            },
            () => {
                this.downtimeSchedules = {
                    total: '0',
                };
            },
        );
    }

    pageChange (page: any): void {
        this.onPageChange(page);
    }

    perPageChange (pageSize: any): void {
        this.onPageSizeChange(pageSize);
    }

    getRecurringTranslation (recurring: any): string {
        switch (recurring) {
            case 'no':
                return this.translateService.getString('None');
            case 'daily':
                return this.translateService.getString('Daily');
            case 'weekly':
                return this.translateService.getString('Weekly');
            case 'monthly':
                return this.translateService.getString('Monthly');
            default:
                return '';
        }
    }

    removeScheduledDowntime (schedule: any): void {
        const text = this.translateService.getString('Are you sure you want to delete this exemption?');
        this.monModalService.confirm(text).then(() => {
            this.uptimeRepoService.destroyScheduledDowntime(this.uptimeId, schedule.id).then(
                () => {
                    this.loadPage();
                },
                () => {
                    // Error could be, because another user deleted it first
                    this.loadPage();
                },
            );
        }, () => {});
    }

    openExemption (schedule?: any): void {
        const params = {
            body: FormUptimeScheduledDowntimeComponent,
            size: 'md',
            data: {
                schedule: schedule || {},
                uptimeId: this.uptimeId,
            },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params, callback: () => this.loadPage() });
    }
}
